/**
 * Iteration 47 — useDownload
 *
 * Two-step download flow that keeps the JWT out of URL query strings:
 *   1. POST /api/downloads/mint  → returns short-lived, single-use ticket
 *   2. Open <base>?ticket=<opaque>&resource_type=...&resource_id=... in the browser
 *
 * Backwards-compatible: if mint fails (e.g. backend not yet upgraded),
 * falls back to the legacy `?token=<JWT>` link with a soft warning to console.
 */
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Build a URL that downloads a resource without exposing the JWT.
 *
 * @param {string} pathTemplate   e.g. `/api/equipment/report.pdf`
 * @param {string} resourceType   e.g. "equipment_report_pdf"
 * @param {string} resourceId     e.g. equipment.id, or "all" for listing exports
 * @param {object} extraQuery     extra query params to preserve (?print=1 etc.)
 * @returns {Promise<string>}     A URL you can put in an <a href=…> or window.open
 */
export async function buildDownloadUrl(pathTemplate, resourceType, resourceId, extraQuery = {}) {
  const queryParts = Object.entries(extraQuery)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);

  try {
    const r = await api.post("/downloads/mint", {
      resource_type: resourceType,
      resource_id: resourceId,
      ttl_seconds: 300,
    });
    const t = r.data.ticket;
    queryParts.push(
      `ticket=${encodeURIComponent(t)}`,
      `resource_type=${encodeURIComponent(resourceType)}`,
      `resource_id=${encodeURIComponent(resourceId)}`,
    );
    return `${BACKEND}${pathTemplate}?${queryParts.join("&")}`;
  } catch (err) {
    // Fallback: legacy ?token= URL. Emit a console warning so the migration
    // is discoverable during Phase 1.
    // eslint-disable-next-line no-console
    console.warn("[useDownload] mint failed, falling back to ?token=", err);
    const jwt = localStorage.getItem("trueline_token") || "";
    queryParts.push(`token=${encodeURIComponent(jwt)}`);
    return `${BACKEND}${pathTemplate}?${queryParts.join("&")}`;
  }
}

/**
 * Convenience: opens the URL in a new tab. Shows a toast on failure.
 */
export async function openDownload(pathTemplate, resourceType, resourceId, extraQuery = {}) {
  try {
    const url = await buildDownloadUrl(pathTemplate, resourceType, resourceId, extraQuery);
    window.open(url, "_blank", "noopener,noreferrer");
  } catch (err) {
    toast.error("Failed to prepare download link");
    // eslint-disable-next-line no-console
    console.error(err);
  }
}
